import React, { useState, useEffect } from 'react'
import {
  Button,
  Collapse,
  Divider,
  Drawer,
  Form,
  Modal,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  theme,
} from 'antd'
import VoiceTextarea from '@/components/VoiceTextarea'
import { HistoryOutlined, RightOutlined, UnorderedListOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import axiosInstance from '@/lib/axios'
import type { WOStatusType } from '@/hooks/useSystemTables'
import { useAuth } from '@/contexts/AuthContext'
import { tonoScheda, useColoriTono } from './colori'
import type { ColoriTono, Tono } from './colori'
import { getRegime } from './regimi'
import type { Regime } from './regimi'

const { Text } = Typography

// ── Funzione pura: tono → colori senza hook ───────────────────────────────────
// Stessa logica dello switch in useColoriTono (colori.ts), ma riceve il token
// già estratto: può essere chiamata dentro un map senza violare le regole degli hook.
interface TokenColori {
  colorPrimary: string; colorPrimaryBg: string; colorPrimaryBorder: string
  colorWarning: string; colorWarningBg: string; colorWarningBorder: string
  colorSuccess: string; colorSuccessBg: string; colorSuccessBorder: string
  colorError: string;   colorErrorBg: string;   colorErrorBorder: string
  colorTextTertiary: string; colorFillTertiary: string; colorBorder: string
}
function coloriDaTonoPura(tono: Tono, t: TokenColori): ColoriTono {
  switch (tono) {
    case 'corso':   return { testo: t.colorPrimary,      sfondo: t.colorPrimaryBg,    bordo: t.colorPrimaryBorder  }
    case 'attesa':  return { testo: t.colorWarning,      sfondo: t.colorWarningBg,    bordo: t.colorWarningBorder  }
    case 'chiuso':  return { testo: t.colorSuccess,      sfondo: t.colorSuccessBg,    bordo: t.colorSuccessBorder  }
    case 'negato':  return { testo: t.colorError,        sfondo: t.colorErrorBg,      bordo: t.colorErrorBorder    }
    default:        return { testo: t.colorTextTertiary, sfondo: t.colorFillTertiary, bordo: t.colorBorder }
  }
}

// ── Gruppi del drawer "Tutti i passaggi" ──────────────────────────────────────
const GRUPPI_DRAWER: Array<{ regime: Regime; titolo: string; sottotitolo: string }> = [
  { regime: 'manuale',     titolo: 'Avanzano a comando',  sottotitolo: 'Serve una tua azione'                        },
  { regime: 'automatico',  titolo: 'Avanzano da soli',    sottotitolo: 'Dipendono dagli interventi'                  },
  { regime: 'chiusura',    titolo: 'Chiusura',            sottotitolo: 'Ultimo passaggio, a carico del front office' },
  { regime: 'fuori linea', titolo: 'Fuori dal percorso',  sottotitolo: 'Deviazioni, non fanno parte del flusso'     },
]

// ── Tipi ──────────────────────────────────────────────────────────────────────

interface Transition {
  to_state: string
  allowed: boolean
  reason_required: boolean
  richiede_commento: boolean
  explanation: string
  // Campi aggiuntivi — opzionali per retro-compatibilità con backend precedente
  blocked_by?: string | null   // "role" | "condition" | null
  blocked_reason?: string      // testo leggibile del motivo del blocco
}

/** Voce dello storico transizioni — include i campi assenti in AuditEntry di V2. */
export interface AuditEntry {
  id: number
  from_state: string | null
  to_state: string
  transition_type: string   // "manual" | "automatic" (valore restituito dal backend)
  executed_by: number | null
  executor_name: string | null
  user_role: string | null
  reason: string | null
  notes: string | null
  created_at: string | null
}

// ── Frasi descrittive per ogni stato ─────────────────────────────────────────

const FRASI: Record<string, string> = {
  bozza: 'La scheda è in fase di compilazione.',
  accettazione: 'Il veicolo è in attesa di accettazione.',
  'in analisi': 'Il veicolo è in fase di analisi e diagnosi.',
  'in approvazione': "Il preventivo è in attesa dell'approvazione del General Manager.",
  approvata: 'Il preventivo è approvato. La lavorazione partirà da sola quando il primo intervento verrà avviato.',
  'in lavorazione': 'I lavori sono in corso. La scheda passerà a completata da sola quando tutti gli interventi saranno conclusi.',
  completata: 'I lavori sono stati completati. Resta da registrare la riconsegna al cliente.',
  riconsegnata: 'Il veicolo è stato riconsegnato al cliente.',
  sospesa: 'La scheda è temporaneamente sospesa.',
  annullata: 'La scheda è stata annullata.',
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface BarraStatoProps {
  workOrderId: number
  currentStato: string
  statusTypes: WOStatusType[]
  interventionsCount?: number
  hasDescrizione?: boolean
  onStateChange?: (newState: string) => void
}

// ── Componente ────────────────────────────────────────────────────────────────

const BarraStato: React.FC<BarraStatoProps> = ({
  workOrderId,
  currentStato,
  statusTypes,
  interventionsCount = 0,
  hasDescrizione = false,
  onStateChange,
}) => {
  const { token } = theme.useToken()
  const colori = useColoriTono(tonoScheda(currentStato))
  const { user } = useAuth()

  // ── Stato locale ───────────────────────────────────────────────────────────

  const [transitions, setTransitions] = useState<Transition[]>([])
  const [auditTrail, setAuditTrail] = useState<AuditEntry[]>([])
  const [loadingTransitions, setLoadingTransitions] = useState(false)
  const [loadingAudit, setLoadingAudit] = useState(false)
  const [auditRefetch, setAuditRefetch] = useState(0)

  const [pendingToState, setPendingToState] = useState<string | undefined>()
  const [transitionInProgress, setTransitionInProgress] = useState(false)
  const [reasonModalOpen, setReasonModalOpen] = useState(false)
  const [commentoModalOpen, setCommentoModalOpen] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const [form] = Form.useForm()
  const [commentoForm] = Form.useForm()

  // ── Fetch transizioni disponibili ──────────────────────────────────────────

  useEffect(() => {
    const params = new URLSearchParams()
    params.append('interventions_count', String(interventionsCount))
    params.append('has_descrizione', hasDescrizione ? 'true' : 'false')

    setLoadingTransitions(true)
    axiosInstance
      .get(`/work-orders/${workOrderId}/available-transitions?${params.toString()}`)
      .then(res => setTransitions(res.data.available_transitions || []))
      .catch(() => setTransitions([]))
      .finally(() => setLoadingTransitions(false))
  }, [workOrderId, currentStato, interventionsCount, hasDescrizione])

  // ── Fetch storico (si riaggiorna dopo ogni transizione) ────────────────────

  useEffect(() => {
    setLoadingAudit(true)
    axiosInstance
      .get(`/work-orders/${workOrderId}/audit-trail?limit=50`)
      .then(res => setAuditTrail(res.data.audit_trail || []))
      .catch(() => setAuditTrail([]))
      .finally(() => setLoadingAudit(false))
  }, [workOrderId, auditRefetch])

  // ── Calcoli derivati ───────────────────────────────────────────────────────

  // Sequenza principale (esclude gli stati fuori linea)
  const phases = [...statusTypes]
    .filter(s => s.nome !== 'sospesa' && s.nome !== 'annullata')
    .sort((a, b) => a.ordine - b.ordine)

  const currentPhaseOrdine =
    phases.find(p => p.nome === currentStato)?.ordine ?? 0
  const currentIdx = phases.findIndex(p => p.nome === currentStato)
  const totale = phases.length

  const fraseBreve = FRASI[currentStato] ?? `Stato corrente: ${currentStato}.`

  const allowedTransitions = transitions.filter(t => t.allowed)

  // Transizioni bloccate da condizioni sui dati (ruolo ok, condizioni no)
  const conditionBlockedTransitions = transitions.filter(
    t => !t.allowed && t.blocked_by === 'condition'
  )
  // true se l'array non è vuoto ma tutte le voci sono riservate ad altri ruoli
  const allBlockedByRole =
    transitions.length > 0 && transitions.every(t => t.blocked_by === 'role')

  // Etichetta del pulsante: explanation descrive l'azione; fallback su nome stato
  const transitionLabel = (t: Transition) =>
    t.explanation || `Passa a ${t.to_state}`

  // Testo motivo del blocco; fallback generico se vuoto
  const blockedReason = (t: Transition) =>
    t.blocked_reason || 'Non disponibile in questo momento'

  // Transizione "in avanti": la prima consentita con ordine > corrente
  const forwardTransitions = [...allowedTransitions]
    .filter(t => {
      const tp = phases.find(p => p.nome === t.to_state)
      return tp !== undefined && tp.ordine > currentPhaseOrdine
    })
    .sort((a, b) => {
      const aO = phases.find(p => p.nome === a.to_state)?.ordine ?? 999
      const bO = phases.find(p => p.nome === b.to_state)?.ordine ?? 999
      return aO - bO
    })

  const primaryTransition = forwardTransitions[0] ?? null

  // "Altri possibili passaggi": tutte le transizioni consentite tranne quella primaria
  const altreTransizioni = allowedTransitions.filter(t => t !== primaryTransition)
  const altreCount = altreTransizioni.length

  // Mappa per lookup rapido nel drawer
  const transitionMap = new Map(transitions.map(t => [t.to_state, t]))

  // Colori per ogni stato — precomputati con funzione pura (nessun hook nel map)
  const coloriPerStato = new Map<string, ColoriTono>(
    statusTypes.map(st => [st.nome, coloriDaTonoPura(tonoScheda(st.nome), token)])
  )

  // ── Esecuzione transizione ─────────────────────────────────────────────────

  const executeTransition = async (
    toState: string,
    reason?: string,
    commentoStato?: string
  ) => {
    setTransitionInProgress(true)
    try {
      // commento_stato sempre incluso nel body — divergenza nota da PhaseProgressBar
      await axiosInstance.post(`/work-orders/${workOrderId}/transition/${toState}`, {
        reason,
        interventions_count: interventionsCount,
        has_descrizione: hasDescrizione,
        commento_stato: commentoStato,
      })
      setPendingToState(undefined)
      setAuditRefetch(c => c + 1)
      onStateChange?.(toState)
    } catch (err: any) {
      Modal.error({
        title: 'Errore transizione',
        content: err?.response?.data?.detail || 'Impossibile eseguire la transizione.',
      })
    } finally {
      setTransitionInProgress(false)
      setReasonModalOpen(false)
      setCommentoModalOpen(false)
      form.resetFields()
      commentoForm.resetFields()
    }
  }

  const handleTransitionClick = (t: Transition) => {
    setPendingToState(t.to_state)
    if (t.reason_required) {
      form.resetFields()
      setReasonModalOpen(true)
    } else if (t.richiede_commento) {
      commentoForm.resetFields()
      setCommentoModalOpen(true)
    } else {
      executeTransition(t.to_state)
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div
      style={{
        background: token.colorBgContainer,
        border: `1px solid ${token.colorBorder}`,
        borderRadius: token.borderRadiusLG,
        padding: '12px 16px',
        marginBottom: 16,
      }}
    >
      {/* Riga alta: frase descrittiva + azioni */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <Text style={{ color: token.colorText, flex: 1 }}>{fraseBreve}</Text>

        <Space size="small" wrap>
          {loadingTransitions ? (
            <Spin size="small" />
          ) : allowedTransitions.length > 0 ? (
            // CASO C — almeno una transizione consentita: pulsante primario attivo
            primaryTransition && (
              <Button
                type="primary"
                icon={<RightOutlined />}
                loading={transitionInProgress && pendingToState === primaryTransition.to_state}
                disabled={transitionInProgress}
                onClick={() => handleTransitionClick(primaryTransition)}
              >
                {transitionLabel(primaryTransition)}
              </Button>
            )
          ) : conditionBlockedTransitions.length > 0 ? (
            // CASO B — ruolo ok ma condizioni sui dati non soddisfatte:
            // pulsante disabilitato con il motivo del blocco (mai il nome stato grezzo)
            <>
              <Tooltip title={blockedReason(conditionBlockedTransitions[0])}>
                <span>
                  <Button disabled icon={<RightOutlined />}>
                    {transitionLabel(conditionBlockedTransitions[0])}
                  </Button>
                </span>
              </Tooltip>
              <Text
                type="secondary"
                style={{ fontSize: 12, maxWidth: 220, display: 'inline-block' }}
              >
                {blockedReason(conditionBlockedTransitions[0])}
              </Text>
            </>
          ) : allBlockedByRole ? (
            // CASO A.role — tutte le transizioni riservate ad altri ruoli
            <Text
              type="secondary"
              style={{ fontSize: 12, maxWidth: 260, display: 'inline-block' }}
            >
              Nessuna azione per il ruolo{' '}
              <b>{user?.ruolo ?? '—'}</b>
            </Text>
          ) : (
            // CASO A.empty — nessuna transizione configurata da questo stato
            <Text
              type="secondary"
              style={{ fontSize: 12, maxWidth: 260, display: 'inline-block' }}
            >
              Nessuna azione disponibile da questo stato
            </Text>
          )}

          <Button
            icon={<UnorderedListOutlined />}
            onClick={() => setDrawerOpen(true)}
          >
            {altreCount > 0 ? `Altri possibili passaggi (${altreCount})` : 'Vedi flusso'}
          </Button>
        </Space>
      </div>

      {/* Sequenza stati — sempre visibile, sostituisce la barra di avanzamento */}
      {totale > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '4px 2px',
            marginTop: 14,
            marginBottom: 10,
          }}
        >
          {/* Stato fuori linea: chip mostrato prima della sequenza */}
          {currentIdx < 0 && (
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: colori.testo,
                background: colori.sfondo,
                border: `1px solid ${colori.bordo}`,
                borderRadius: token.borderRadiusSM,
                padding: '1px 8px',
                marginRight: 6,
                whiteSpace: 'nowrap',
                lineHeight: '20px',
              }}
            >
              {currentStato}
            </span>
          )}
          {phases.map((st, idx) => {
            const isCurrent = st.nome === currentStato
            // isPast è false anche quando fuori linea (currentIdx === -1)
            const isPast = currentIdx >= 0 && idx < currentIdx
            const textColor = isCurrent
              ? colori.testo
              : isPast
              ? token.colorTextTertiary
              : token.colorTextQuaternary
            return (
              <React.Fragment key={st.nome}>
                {idx > 0 && (
                  <span
                    aria-hidden
                    style={{
                      color: token.colorBorderSecondary,
                      fontSize: 11,
                      userSelect: 'none',
                      flexShrink: 0,
                      lineHeight: 1,
                    }}
                  >
                    ›
                  </span>
                )}
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: isCurrent ? 600 : 400,
                    color: textColor,
                    background: isCurrent ? colori.sfondo : 'transparent',
                    border: `1px solid ${isCurrent ? colori.bordo : 'transparent'}`,
                    borderRadius: token.borderRadiusSM,
                    padding: '1px 6px',
                    whiteSpace: 'nowrap',
                    lineHeight: '20px',
                  }}
                >
                  {st.nome}
                </span>
              </React.Fragment>
            )
          })}
        </div>
      )}

      {/* Storico collassabile */}
      <div style={{ marginTop: 8 }}>
        <Collapse
          ghost
          size="small"
          items={[
            {
              key: 'storico',
              label: (
                <Space size={6}>
                  <HistoryOutlined style={{ color: token.colorTextSecondary }} />
                  <Text style={{ fontSize: 13, color: token.colorText }}>
                    Storico cambi di stato ({auditTrail.length})
                  </Text>
                </Space>
              ),
              children: loadingAudit ? (
                <Spin size="small" />
              ) : auditTrail.length === 0 ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Nessuna transizione registrata.
                </Text>
              ) : (
                <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                  {auditTrail.map(entry => (
                    <div
                      key={entry.id}
                      style={{
                        padding: '6px 0',
                        borderBottom: `1px solid ${token.colorBorderSecondary}`,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '4px 12px',
                        alignItems: 'flex-start',
                      }}
                    >
                      <Text
                        style={{
                          fontSize: 11,
                          color: token.colorTextSecondary,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {entry.created_at
                          ? dayjs(entry.created_at).format('DD/MM/YY HH:mm')
                          : '—'}
                      </Text>

                      {/* Alcune righe registrano un fatto avvenuto DENTRO la scheda
                          (es. il consenso a riprendere un intervento fermo per una RDA)
                          senza che lo stato cambi: from_state e to_state coincidono.
                          Mostrare "X → X" sembrerebbe un errore — si mostra lo stato una
                          volta sola, la spiegazione e' nel reason qui sotto. */}
                      <Text style={{ fontSize: 12, color: token.colorText }}>
                        {entry.from_state === entry.to_state ? (
                          <b>{entry.to_state}</b>
                        ) : (
                          <>
                            <b>{entry.from_state ?? '—'}</b>
                            {' → '}
                            <b>{entry.to_state}</b>
                          </>
                        )}
                      </Text>

                      {entry.executor_name && (
                        <Text
                          style={{ fontSize: 12, color: token.colorTextSecondary }}
                        >
                          {entry.executor_name}
                          {entry.user_role ? ` (${entry.user_role})` : ''}
                        </Text>
                      )}

                      <Tag
                        color={entry.transition_type === 'automatic' ? 'blue' : 'default'}
                        style={{ fontSize: 10, lineHeight: '18px', margin: 0 }}
                      >
                        {entry.transition_type === 'automatic' ? 'automatico' : 'manuale'}
                      </Tag>

                      {entry.reason && (
                        <Text
                          style={{
                            fontSize: 11,
                            color: token.colorTextSecondary,
                            width: '100%',
                          }}
                        >
                          {entry.reason}
                        </Text>
                      )}
                    </div>
                  ))}
                </div>
              ),
            },
          ]}
        />
      </div>

      {/* Drawer: tutti i passaggi del flusso, raggruppati per regime */}
      <Drawer
        title="Tutti i passaggi"
        placement="right"
        width={300}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        {GRUPPI_DRAWER.map((gruppo, gruppoIdx) => {
          // getRegime restituisce sempre un Regime valido (fallback 'manuale').
          // Tutti gli stati di statusTypes compaiono — nessuno viene scartato.
          const voci = [...statusTypes]
            .filter(st => getRegime(st.nome) === gruppo.regime)
            .sort((a, b) => a.ordine - b.ordine)
          if (voci.length === 0) return null

          return (
            <div key={gruppo.regime} style={{ marginBottom: 16 }}>
              {gruppoIdx > 0 && <Divider style={{ margin: '0 0 12px' }} />}
              <div style={{
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: token.colorTextTertiary,
                marginBottom: 2,
              }}>
                {gruppo.titolo}
              </div>
              <div style={{
                fontSize: 12,
                color: token.colorTextQuaternary,
                marginBottom: 8,
              }}>
                {gruppo.sottotitolo}
              </div>
              <Space direction="vertical" style={{ width: '100%' }} size={4}>
                {voci.map(st => {
                  const t = transitionMap.get(st.nome)
                  const isCurrent = st.nome === currentStato
                  const isAllowed = t?.allowed === true
                  const isBlocked = t !== undefined && !t.allowed
                  // Etichetta azione: explanation, mai il nome interno dello stato
                  const btnLabel = t
                    ? (t.explanation || `Passa a ${st.nome}`)
                    : st.nome
                  // Motivo del blocco — sostituisce explanation (che descrive l'azione, non il blocco)
                  const blockText = isBlocked
                    ? (t.blocked_reason || 'Non disponibile in questo momento')
                    : undefined
                  const c = coloriPerStato.get(st.nome)

                  return (
                    <div key={st.nome}>
                      {isCurrent ? (
                        // Stato corrente: "sei qui" — non cliccabile ma visivamente
                        // distinto da "non puoi farlo". Usiamo un div con colori
                        // semantici invece di <Button disabled> (che li sovrascriverebbe).
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%',
                          padding: '4px 15px',
                          minHeight: 32,
                          background: c?.sfondo ?? token.colorFillTertiary,
                          border: `1px solid ${c?.bordo ?? token.colorBorder}`,
                          borderRadius: token.borderRadius,
                          cursor: 'default',
                          boxSizing: 'border-box',
                        }}>
                          {/* Pallino 10px con anello */}
                          <span style={{
                            display: 'inline-block',
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            background: c?.testo ?? token.colorTextTertiary,
                            boxShadow: `0 0 0 3px ${c?.sfondo ?? token.colorFillTertiary}`,
                            marginRight: 8,
                            flexShrink: 0,
                          }} />
                          <span style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: c?.testo ?? token.colorText,
                            flex: 1,
                          }}>
                            {st.nome}
                          </span>
                          <span style={{
                            fontSize: 11,
                            color: c?.testo ?? token.colorTextTertiary,
                            marginLeft: 6,
                            flexShrink: 0,
                          }}>
                            ← attuale
                          </span>
                        </div>
                      ) : (
                        <Tooltip title={blockText}>
                          {/* span wrapper: permette tooltip su Button disabilitato */}
                          <span style={{ display: 'block' }}>
                            <Button
                              block
                              disabled={!isAllowed}
                              loading={transitionInProgress && pendingToState === st.nome}
                              onClick={() => {
                                if (!isAllowed || !t) return
                                setDrawerOpen(false)
                                handleTransitionClick(t)
                              }}
                              style={{ textAlign: 'left' }}
                            >
                              {/* Pallino colorato: colore semantico dal tema, non da st.colore del DB */}
                              <span style={{
                                display: 'inline-block',
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                background: c?.testo ?? token.colorTextTertiary,
                                marginRight: 6,
                                flexShrink: 0,
                                verticalAlign: 'middle',
                              }} />
                              {btnLabel}
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                      {isBlocked && (
                        <Text
                          style={{
                            fontSize: 11,
                            color: token.colorTextSecondary,
                            display: 'block',
                            marginTop: 2,
                            paddingLeft: 4,
                          }}
                        >
                          {blockText}
                        </Text>
                      )}
                    </div>
                  )
                })}
              </Space>
            </div>
          )
        })}
      </Drawer>

      {/* Modal: motivazione */}
      <Modal
        title="Motivazione richiesta"
        open={reasonModalOpen}
        onCancel={() => {
          setReasonModalOpen(false)
          setPendingToState(undefined)
          form.resetFields()
        }}
        onOk={() => form.submit()}
        confirmLoading={transitionInProgress}
        okText="Conferma"
        cancelText="Annulla"
      >
        <Form
          form={form}
          onFinish={values =>
            pendingToState && executeTransition(pendingToState, values.reason as string)
          }
        >
          <Form.Item
            name="reason"
            rules={[{ required: true, message: 'Inserisci una motivazione' }]}
          >
            <VoiceTextarea
              rows={4}
              minHeight={100}
              maxHeight={220}
              placeholder="Motivazione per questa transizione…"
              label="Motivo sospensione"
              compact
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal: commento stato */}
      <Modal
        title="Commento stato"
        open={commentoModalOpen}
        onCancel={() => {
          setCommentoModalOpen(false)
          setPendingToState(undefined)
          commentoForm.resetFields()
        }}
        onOk={() => commentoForm.submit()}
        confirmLoading={transitionInProgress}
        okText="Conferma"
        cancelText="Annulla"
      >
        <Form
          form={commentoForm}
          onFinish={values =>
            pendingToState &&
            executeTransition(pendingToState, undefined, values.commento_stato as string)
          }
        >
          <Form.Item
            name="commento_stato"
            rules={[{ required: true, message: 'Inserisci un commento' }]}
          >
            <VoiceTextarea
              rows={4}
              minHeight={100}
              maxHeight={220}
              placeholder="Commento per questo cambio di stato…"
              label="Commento"
              compact
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default BarraStato
